// Hand-rolled SVG line chart. We don't need a charting library for one
// line, a zero baseline, an event marker and a trough callout — and we
// keep the bundle small.

import type { Cashflow } from '../lib/cashflow';
import { formatMoney } from '../lib/currency';

type Props = {
  cashflow: Cashflow;
  currency: string;
};

const W = 760;
const H = 240;
const PAD_L = 70;
const PAD_R = 16;
const PAD_T = 18;
const PAD_B = 32;

export default function CashflowChart({ cashflow, currency }: Props) {
  if (cashflow.weeks.length < 2) {
    return (
      <div className="muted py-20">
        Add at least two weeks of activity to see the cashflow line.
      </div>
    );
  }

  const ys = cashflow.weeks.map((w) => w.cumulative);
  const yMin = Math.min(0, ...ys);
  const yMax = Math.max(0, ...ys);
  const xMax = cashflow.weeks.length - 1;

  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const xScale = (i: number) => PAD_L + (i / (xMax || 1)) * innerW;
  const yScale = (v: number) =>
    PAD_T + (1 - (v - yMin) / (yMax - yMin || 1)) * innerH;
  const yZero = yScale(0);

  const path = cashflow.weeks
    .map((w, i) => `${i === 0 ? 'M' : 'L'}${xScale(i).toFixed(1)},${yScale(w.cumulative).toFixed(1)}`)
    .join(' ');
  const areaPath =
    `${path} L${xScale(xMax).toFixed(1)},${yZero.toFixed(1)} L${xScale(0).toFixed(1)},${yZero.toFixed(1)} Z`;

  const eventIdx = cashflow.eventWeekStart
    ? cashflow.weeks.findIndex((w) => w.weekStart === cashflow.eventWeekStart)
    : cashflow.weeks.findIndex((w) => w.daysToEvent <= 0);
  const eventX = eventIdx >= 0 ? xScale(eventIdx) : null;

  const troughIdx = cashflow.trough
    ? cashflow.weeks.findIndex((w) => w.weekStart === cashflow.trough!.date)
    : -1;
  const troughHasGap = cashflow.trough !== null && cashflow.trough.amount < 0;

  // X-axis: 4 evenly spaced labels.
  const xTicks = pickTicks(xMax, 4);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="cashflow-svg" role="img" aria-label="Cashflow over time">
      {/* zero baseline */}
      <line x1={PAD_L} x2={W - PAD_R} y1={yZero} y2={yZero} className="cf-axis-zero" />

      {/* event-date marker */}
      {eventX !== null && (
        <>
          <line x1={eventX} x2={eventX} y1={PAD_T} y2={H - PAD_B} className="cf-event-marker" />
          <text x={eventX} y={PAD_T - 4} className="cf-event-label" textAnchor="middle">
            EVENT
          </text>
        </>
      )}

      {/* line + soft area under */}
      <path d={areaPath} className={troughHasGap ? 'cf-area neg' : 'cf-area'} />
      <path d={path} className={troughHasGap ? 'cf-line neg' : 'cf-line'} />

      {/* trough marker */}
      {troughIdx >= 0 && cashflow.trough && (
        <>
          <circle
            cx={xScale(troughIdx)}
            cy={yScale(cashflow.trough.amount)}
            r={5}
            className={troughHasGap ? 'cf-trough-dot neg' : 'cf-trough-dot'}
          />
          <text
            x={xScale(troughIdx)}
            y={yScale(cashflow.trough.amount) + (troughHasGap ? 18 : -10)}
            className={troughHasGap ? 'cf-trough-label neg' : 'cf-trough-label'}
            textAnchor="middle"
          >
            {formatMoney(cashflow.trough.amount, currency)}
          </text>
        </>
      )}

      {/* y-axis labels: max, zero, min */}
      <text x={PAD_L - 8} y={yScale(yMax) + 4} className="cf-axis-label" textAnchor="end">
        {formatMoney(yMax, currency)}
      </text>
      <text x={PAD_L - 8} y={yZero + 4} className="cf-axis-label" textAnchor="end">
        {formatMoney(0, currency)}
      </text>
      {yMin < 0 && (
        <text x={PAD_L - 8} y={yScale(yMin) + 4} className="cf-axis-label" textAnchor="end">
          {formatMoney(yMin, currency)}
        </text>
      )}

      {/* x-axis labels */}
      {xTicks.map((i) => (
        <text key={i} x={xScale(i)} y={H - 10} className="cf-axis-label" textAnchor="middle">
          {shortDate(cashflow.weeks[i].weekStart)}
        </text>
      ))}
    </svg>
  );
}

function pickTicks(xMax: number, count: number): number[] {
  if (xMax <= 0) return [0];
  if (xMax + 1 <= count) return Array.from({ length: xMax + 1 }, (_, i) => i);
  const out: number[] = [];
  for (let k = 0; k < count; k++) {
    out.push(Math.round((k * xMax) / (count - 1)));
  }
  return out;
}

function shortDate(iso: string): string {
  // YYYY-MM-DD → "MMM dd" (e.g. 2026-08-22 → Aug 22)
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', { month: 'short', day: '2-digit', timeZone: 'UTC' });
}
